
import React, { useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import UpgradePrompt from './UpgradePrompt';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface ProFeatureWrapperProps {
  children: React.ReactNode;
  feature: string;
  description?: string;
  fallback?: React.ReactNode;
}

const ProFeatureWrapper: React.FC<ProFeatureWrapperProps> = ({ 
  children, 
  feature, 
  description,
  fallback 
}) => {
  const { isProUser, subscription, loading } = useSubscription();
  const proFeaturesEnabled = useFeatureFlag('enableProFeatures');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  // Check specific feature flags for AI features
  const aiAnalysisEnabled = useFeatureFlag('enableAIAnalysis');
  const liveGuidanceEnabled = useFeatureFlag('enableLiveGuidance');

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If feature flags disable pro features, always show the content
  if (!proFeaturesEnabled) {
    return <>{children}</>;
  }

  // Check specific AI feature flags
  if (feature === 'AI Analysis' && !aiAnalysisEnabled) {
    return <>{children}</>;
  }

  if (feature === 'Live Guidance' && !liveGuidanceEnabled) {
    return <>{children}</>;
  }

  // Define which features require which tiers
  const expertOnlyFeatures = ['Live Guidance', 'Team Collaboration', 'Priority Support'];
  const proOrExpertFeatures = ['AI Analysis'];

  // Check if feature requires expert tier
  if (expertOnlyFeatures.includes(feature)) {
    if (subscription?.subscription_tier === 'expert') {
      return <>{children}</>;
    }
  } 
  // Check if feature requires pro or expert tier
  else if (proOrExpertFeatures.includes(feature)) {
    if (isProUser) { // isProUser covers both pro and expert
      return <>{children}</>;
    }
  }
  // If user has any paid tier, show the content normally
  else if (isProUser) {
    return <>{children}</>;
  }

  // For free users or insufficient tier, wrap the content to show upgrade prompt
  const handleInteraction = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUpgradePrompt(true);
  };

  return (
    <>
      <div onClick={handleInteraction} className="cursor-pointer">
        {children}
      </div>
      
      <Dialog open={showUpgradePrompt} onOpenChange={setShowUpgradePrompt}>
        <DialogContent className="p-0 border-0 bg-transparent shadow-none max-w-md">
          <UpgradePrompt 
            feature={feature} 
            description={description}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProFeatureWrapper;
